import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useAppSelector } from '@/store';
import { selectNShowcase } from '@/store/slices/userPreferences';

import { Icon, type IconName } from '../../../../components';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import { useMailboxEligibility } from '../../../mailbox/useMailboxEligibility';
import { COLORS, HUB_INK, type HubInkHue } from '../../../../theme/tokens';
import { HubGlyph } from '../../../website/components/HubGlyph';
import { SettingsGlyph, type SettingsGlyphName } from '../SettingsGlyph';
import { GroupDivider, ICON_PX, Lead, List, Row, RowLabel, Tile } from './SettingsList.styles';

export type SettingsSection =
  | 'secure-brand'
  | 'profile'
  | 'website-settings'
  | 'mailbox'
  | 'interface'
  | 'billing'
  | 'account'
  | 'communications'
  | 'privacy'
  | 'contact';

/**
 * Static section copy (English keys, translation-ready). Shared with the BackHeader.
 * `website-settings` and `secure-brand` are personalised with the active brand name at
 * render time (see {@link sectionLabel}); the labels here are the brand-less fallbacks.
 * Kept as stable English ids, translated at each render site via {@link sectionLabel}.
 */
export const SECTION_LABELS: Record<SettingsSection, string> = {
  'secure-brand': 'Secure your brand',
  profile: 'Profile',
  'website-settings': 'Website Settings',
  // Deliberately provider-neutral: the mail provider is an implementation detail behind our own
  // endpoints (brand-mailbox API §1), so the operator-facing label names no vendor.
  mailbox: 'Mailbox',
  interface: 'Interface',
  billing: 'Billing Information',
  account: 'Account & Sign In',
  communications: 'Communication Preferences',
  privacy: 'Privacy',
  contact: 'Contact',
};

/** Section key → translated label, evaluated reactively via the passed `t`. */
const translatedSectionLabel = (t: TFunction, section: SettingsSection): string => {
  switch (section) {
    case 'secure-brand':
      return t('Secure your brand');
    case 'profile':
      return t('Profile');
    case 'website-settings':
      return t('Website Settings');
    case 'mailbox':
      return t('Mailbox');
    case 'interface':
      return t('Interface');
    case 'billing':
      return t('Billing Information');
    case 'account':
      return t('Account & Sign In');
    case 'communications':
      return t('Communication Preferences');
    case 'privacy':
      return t('Privacy');
    case 'contact':
      return t('Contact');
    default:
      return t(SECTION_LABELS[section]);
  }
};

/**
 * Once the active brand is known, the Website Settings row + drill-in title reads "{Brand} Settings"
 * (e.g. "NeonJack Settings") and Secure your brand reads "Secure your {Brand} brand"; every other
 * section uses its static {@link SECTION_LABELS} label. With `secured` (the brand's declaration is
 * signed), Secure your brand reads "Your {Brand} brand is secured" instead. Translated at the call
 * site via the passed `t` (the English {@link SECTION_LABELS} value is the i18n key).
 */
export const sectionLabel = (t: TFunction, section: SettingsSection, brandName?: string, secured = false): string => {
  if (section === 'secure-brand' && secured) {
    return brandName ? t('Your {{brandName}} brand is secured', { brandName }) : t('Your brand is secured');
  }
  if (brandName && section === 'website-settings') return t('{{brandName}} Settings', { brandName });
  if (brandName && section === 'secure-brand') return t('Secure your {{brandName}} brand', { brandName });
  return translatedSectionLabel(t, section);
};

/**
 * The index's groups, top to bottom, split by fading hairlines: Secure your brand on its own, then
 * the brand-level sections, then the account-level ones. Secure your brand and the brand mail
 * section are flag-gated; a group with no visible row drops out along with its divider, so a gated
 * section never leaves a stray hairline behind.
 *
 * Contact has no row: its support form does not work yet, so the index offers no way in.
 */
type ListedSection = Exclude<SettingsSection, 'contact'>;

const GROUPS: readonly (readonly ListedSection[])[] = [
  ['secure-brand'],
  ['website-settings', 'mailbox'],
  ['profile', 'interface', 'communications', 'privacy', 'billing', 'account'],
];

/** A row's tile glyph: one of the shared `Icon`s or one of the index's own (`SettingsGlyph`), its
 *  viewBox width (the ink sweeps across exactly that span) and the hub hue it is painted in. */
type RowGlyph = {
  glyph: { kind: 'icon'; name: IconName } | { kind: 'own'; name: SettingsGlyphName };
  span: number;
  hue: HubInkHue;
};

const ROW_GLYPH: Record<ListedSection, RowGlyph> = {
  'secure-brand': { glyph: { kind: 'icon', name: 'shield' }, span: 24, hue: 'green' },
  'website-settings': { glyph: { kind: 'icon', name: 'settings' }, span: 24, hue: 'orange' },
  mailbox: { glyph: { kind: 'icon', name: 'mail' }, span: 24, hue: 'cyan' },
  profile: { glyph: { kind: 'icon', name: 'user' }, span: 24, hue: 'violet' },
  interface: { glyph: { kind: 'icon', name: 'palette' }, span: 24, hue: 'blue' },
  communications: { glyph: { kind: 'own', name: 'chat' }, span: 24, hue: 'pink' },
  privacy: { glyph: { kind: 'icon', name: 'eye-off' }, span: 24, hue: 'green' },
  billing: { glyph: { kind: 'icon', name: 'wallet' }, span: 26, hue: 'orange' },
  account: { glyph: { kind: 'icon', name: 'lock' }, span: 24, hue: 'violet' },
};

/** Where FTUX - Getting Started finds the Mailbox row, the way to the mail piece (inert outside an FTUX step). */
const MAIL_ROW_TARGET: FtuxTargetId = 'mail-row';

export type SettingsListProps = {
  /** Opens a section — the key is read from `event.currentTarget.dataset.id`. */
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  /** Active brand name, personalising the Website Settings and Secure your brand rows. */
  brandName?: string;
  /** The brand's declaration is signed: the Secure your brand row says it is secured. */
  brandSecured?: boolean;
};

/** Settings index — one tappable row per section, each with its glyph tile, drilling into its detail. */
export const SettingsList = ({ onOpen, brandName, brandSecured = false }: SettingsListProps): React.ReactElement => {
  const { t } = useTranslation();
  // The flag-gated brand sections (Secure your brand and the brand mail section) drop out of the
  // list until enabled; non-flag section ids resolve to enabled, so they are unaffected.
  const { isEnabled } = useFeatureGate();
  // The Newton showcase shows no billing details, so Billing Information drops out too.
  const newtonShowcase = useAppSelector(selectNShowcase);
  // Asked as Settings opens: the Mailbox row shows only once ows-mail says the brand's domain may have mail.
  const mailEligible = useMailboxEligibility() === 'eligible';
  const groups = GROUPS.map((group) =>
    group.filter(
      (key) => isEnabled(key) && !(newtonShowcase && key === 'billing') && !(key === 'mailbox' && !mailEligible),
    ),
  ).filter((group) => group.length > 0);
  // Each group's first row's place in the visible order: the rows' entrance steps run on across the groups.
  const firstIndex = groups.map((_, index) => groups.slice(0, index).reduce((count, group) => count + group.length, 0));
  return (
    <List>
      {groups.map((group, index) => (
        <React.Fragment key={group[0]}>
          {index > 0 && <GroupDivider variant="styled" $enterIndex={firstIndex[index]} />}
          {group.map((key, rowIndex) => {
            const { glyph, span, hue } = ROW_GLYPH[key];
            return (
              <Row
                key={key}
                type="button"
                data-id={key}
                data-ftux={key === 'mailbox' ? MAIL_ROW_TARGET : undefined}
                onClick={onOpen}
                $enterIndex={firstIndex[index] + rowIndex}
              >
                <Lead>
                  <Tile aria-hidden="true">
                    <HubGlyph ink={HUB_INK[hue]} span={span}>
                      {(ink) =>
                        glyph.kind === 'icon' ? (
                          <Icon name={glyph.name} size={ICON_PX} color={ink} />
                        ) : (
                          <SettingsGlyph name={glyph.name} size={ICON_PX} color={ink} />
                        )
                      }
                    </HubGlyph>
                  </Tile>
                  <RowLabel text={sectionLabel(t, key, brandName, brandSecured)} tag="span" />
                </Lead>
                <Icon name="arrow-right-circle" size={24} color={COLORS.iconBright} />
              </Row>
            );
          })}
        </React.Fragment>
      ))}
    </List>
  );
};

export default SettingsList;
